import type { CompareStep } from './compare';
import type {
  FaqContent,
  FeatureBlockContent,
  HeroContent,
  Stat,
  TextSectionContent,
} from './types';

export const travelerHero: HeroContent = {
  title: 'The card for\nall your travels',
  cta: { label: 'Get your nsave card', href: '/get-the-app' },
  image: {
    src: '/images/traveler/hero.jpg',
    webpSrc: '/images/traveler/hero.webp',
    // The mobile design crops in on the traveller rather than the lounge.
    mobileSrc: '/images/traveler/hero-mobile.jpg',
    mobileWebpSrc: '/images/traveler/hero-mobile.webp',
    alt: 'Traveller in an airport lounge checking a phone beside a tan leather suitcase',
  },
};

export const travelerStats: Stat[] = [
  { icon: 'star', value: '4.4 ★', label: 'On the App Store' },
  { icon: 'users', value: '500k', label: 'nsave users' },
  { icon: 'at-sign', value: '250k', label: 'Community members' },
  { icon: 'globe', value: '23+', label: 'Supported countries' },
  { icon: 'bank', value: '1m+', label: 'Transactions' },
];

export const travelerIntro: TextSectionContent = {
  title: 'A better way to\nexperience the world',
  body: "Whether you're travelling for work, exploring somewhere new, or returning to a place you love, every journey comes with its own possibilities. nsave brings together everyday convenience and thoughtful card benefits, so you can focus on making the most of the experience.",
};

export interface TravelerPartnerLogo {
  name: string;
  src: string;
  /** Rendered size in px, from the Figma logo strip. */
  width: number;
  height: number;
  /** The mobile strip only has room for the first four logos. */
  desktopOnly?: boolean;
}

export const travelerPartnerLogos: TravelerPartnerLogo[] = [
  { name: 'Visa', src: '/logos/traveler/visa.svg', width: 40.51, height: 13.08 },
  { name: 'GigSky', src: '/logos/traveler/gigsky.svg', width: 68, height: 13.08 },
  { name: 'Global Blue', src: '/logos/traveler/global-blue.svg', width: 115.28, height: 13.08 },
  { name: 'SIXT', src: '/logos/traveler/sixt.svg', width: 31.87, height: 13.08 },
  { name: 'AIG', src: '/logos/traveler/aig.png', width: 26, height: 14, desktopOnly: true },
  {
    name: 'Banyan Group',
    src: '/logos/traveler/banyan-group.png',
    width: 115.41,
    height: 17.93,
    desktopOnly: true,
  },
];

export const travelerBookingSteps: CompareStep[] = [
  {
    title: 'Book your next getaway',
    description:
      'Pay for flights, hotels, and travel experiences with your nsave USD card, online or in-store across 170+ countries wherever Visa is accepted.',
    image: {
      src: '/images/traveler/booking-getaway.jpg',
      webpSrc: '/images/traveler/booking-getaway.webp',
      alt: 'Ornate heritage hotel with red domes against a clear blue sky',
    },
  },
  {
    title: 'Spend in local currencies',
    description:
      'Use your nsave card to pay in local currencies while traveling abroad, from everyday purchases, dining and experiences at your destination.',
    image: {
      src: '/images/traveler/booking-local-currency.jpg',
      webpSrc: '/images/traveler/booking-local-currency.webp',
      alt: 'Restaurant dining room with set tables in front of wooden shelving',
    },
  },
  {
    title: 'Tap and go',
    description:
      'Add your card to Apple Pay or Google Pay for convenient contactless payments on your travels, at restaurants, shops, hotels, and more.',
    image: {
      src: '/images/traveler/booking-tap.jpg',
      webpSrc: '/images/traveler/booking-tap.webp',
      alt: 'Hand holding a phone showing the red nsave Visa card in a mobile wallet',
    },
  },
];

export interface TravelerTierTag {
  label: string;
  tier: 'platinum' | 'infinite';
}

export const travelerConfidenceTags: TravelerTierTag[] = [
  { label: 'Platinum', tier: 'platinum' },
  { label: 'Infinite', tier: 'infinite' },
];

export const travelerConfidence: FeatureBlockContent = {
  title: 'Travel with Confidence',
  image: {
    src: '/images/traveler/confidence.jpg',
    webpSrc: '/images/traveler/confidence.webp',
    alt: 'Traveller reading a tablet in a window seat on a plane',
  },
  rows: [
    {
      icon: 'lock',
      title: 'Purchase & Warranty Protection',
      description:
        'Protect eligible purchases against theft or damage, get reimbursed for price differences within 30 days, and enjoy up to one additional year of warranty coverage.',
    },
    {
      icon: 'shield',
      title: 'Rental Car Protection',
      description:
        'Get worldwide coverage against collision, theft, and vandalism on qualifying rentals when you pay with your card.',
    },
    {
      icon: 'globe',
      title: 'Stay Connected Abroad',
      description: 'Enjoy up to 3 days of complimentary GigSky eSIM data in select countries.',
    },
  ],
};

export const travelerPerksTags: TravelerTierTag[] = [
  { label: 'Exclusive to Infinite', tier: 'infinite' },
];

export const travelerPerks: FeatureBlockContent = {
  title: 'Exclusive Travel Perks',
  image: {
    src: '/images/traveler/perks.jpg',
    webpSrc: '/images/traveler/perks.webp',
    alt: 'Couple dining on a terrace overlooking yachts on the sea at sunset',
  },
  rows: [
    {
      icon: 'ticket',
      title: 'Airport Lounge Access',
      description:
        'Access 1,200+ VIP lounges across 460+ airports in 141 countries through Visa Airport Companion.',
    },
    {
      icon: 'briefcase',
      title: 'Travel & Baggage Protection',
      description:
        'Enjoy up to $1.5 million in eligible travel accident coverage, plus protection of up to $600 for baggage delays and $3,000 for lost luggage.',
    },
    {
      icon: 'star',
      title: 'Luxury Hotel Privileges',
      description:
        'Enjoy exclusive benefits at 900+ hotels, including complimentary breakfast, room upgrades when available, and late checkout.',
    },
  ],
};

export const travelerDiscoverSteps: CompareStep[] = [
  {
    title: 'Explore Paris',
    description:
      'Enjoy eligible priority access to the iconic Louvre Museum and exclusive shopping privileges at participating destinations across Paris.',
    image: {
      src: '/images/traveler/discover-paris.jpg',
      webpSrc: '/images/traveler/discover-paris.webp',
      alt: 'The Louvre glass pyramid in Paris',
    },
  },
  {
    title: 'Adventure in Dubai',
    description:
      'Unlock special offers on exciting desert adventures, unique local experiences, and memorable activities during your stay in Dubai.',
    image: {
      src: '/images/traveler/discover-dubai.jpg',
      webpSrc: '/images/traveler/discover-dubai.webp',
      alt: 'Sand dunes in the desert outside Dubai',
    },
  },
  {
    title: 'Unwind in Thailand',
    description:
      'Enjoy exclusive discounts on wellness retreats, complimentary dining, room upgrades, and wellness credits for a relaxing getaway.',
    image: {
      src: '/images/traveler/discover-thailand.jpg',
      webpSrc: '/images/traveler/discover-thailand.webp',
      alt: 'Tropical resort villas surrounded by palm trees in Thailand',
    },
  },
];

// The Figma only has the questions. These answers are drafted from this page's
// own copy and need product/compliance sign-off before launch.
export const travelerFaq: FaqContent = {
  title: 'Frequently asked Questions',
  items: [
    {
      question: 'Can I use my nsave card internationally?',
      answer:
        'Yes. Your nsave card works online and in-store across 170+ countries, wherever Visa is accepted.',
    },
    {
      question: 'Can I pay in currencies other than USD?',
      answer:
        'Yes. You can use your nsave card to pay in local currencies while travelling abroad. A small currency conversion fee applies for spending outside your account currency.',
    },
    {
      question: 'Can I use Apple Pay or Google Pay while travelling?',
      answer:
        'Yes. Add your card to Apple Pay or Google Pay for contactless payments on your travels, at restaurants, shops, hotels, and more.',
    },
    {
      question: "What's the difference between Visa Platinum and Visa Infinite?",
      answer:
        'Both cards include purchase and warranty protection, rental car protection and complimentary GigSky eSIM data. Visa Infinite adds airport lounge access, travel and baggage protection, and luxury hotel privileges.',
    },
    {
      question: 'Does my nsave card include airport lounge access?',
      answer:
        'Airport lounge access is exclusive to Visa Infinite, with access to 1,200+ VIP lounges across 460+ airports in 141 countries through Visa Airport Companion.',
    },
    {
      question: 'How do I access the travel benefits and exclusive offers?',
      answer:
        'Your card’s travel benefits and exclusive offers are available to eligible cardholders. Open the nsave app to see the benefits included with your card.',
    },
  ],
};

export const travelerFaqFootnote =
  "Please note: the nsave Card is a distinct, standalone product and operates separately from nsave's other accounts and services. The card can be used everywhere Visa is accepted";
